<template>
  <div class="liked-page">
    <!-- Başlık -->
    <header class="liked-page__head">
      <span class="liked-page__icon">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
      </span>
      <div>
        <h1 class="liked-page__title">
          {{ $t('Users I like') }}
        </h1>
        <p
          v-if="totalCount !== null"
          class="liked-page__subtitle"
        >
          {{ $t('likedUsers.subtitle', { count: activeCount }) }}
        </p>
      </div>
    </header>

    <!-- İlk yükleme -->
    <div
      v-if="loading && users.length === 0"
      class="liked-page__state"
    >
      <loading-spinner />
    </div>

    <!-- Boş liste -->
    <div
      v-else-if="!loading && users.length === 0"
      class="liked-page__empty"
    >
      <span class="liked-page__empty-icon">
        <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
      </span>
      <p>{{ $t('likedUsers.empty') }}</p>
      <router-link
        :to="{ name: 'moments' }"
        class="liked-btn liked-btn--primary"
      >
        {{ $t('likedUsers.emptyCta') }}
      </router-link>
    </div>

    <!-- Liste -->
    <div
      v-else
      class="liked-grid"
    >
      <article
        v-for="user in users"
        :key="user.id"
        class="liked-card"
      >
        <router-link
          :to="profileRoute(user)"
          class="liked-card__photo"
          :aria-label="$t('likedUsers.viewProfile')"
        >
          <img
            v-if="user.profile && user.profile.image"
            :src="user.profile.image"
            :alt="user.userName"
            loading="lazy"
          >
          <span
            v-else
            class="liked-card__initial"
          >{{ (user.userName || '?').charAt(0).toUpperCase() }}</span>

          <span
            v-if="isOnline(user)"
            class="liked-card__online"
          >
            <i />{{ $t('likedUsers.online') }}
          </span>

          <span class="liked-card__info">
            <strong>
              {{ user.userName }}<template v-if="user.profile && user.profile.year">, {{ age(user) }}</template>
            </strong>
            <small v-if="user.profile && user.profile.country">{{ $t('countries.' + user.profile.country) }}</small>
          </span>
        </router-link>

        <div class="liked-card__actions"
        >
          <router-link
            :to="{ name: 'chat', params: { userId: user.id } }"
            class="liked-btn liked-btn--ghost liked-card__message"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            {{ $t('likedUsers.message') }}
          </router-link>
          <button
            type="button"
            class="liked-card__heart"
            :title="$t('likedUsers.unlike')"
            :aria-label="$t('likedUsers.unlike')"
            :disabled="isBusy(user)"
            @click="unlike(user)"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
          </button>
        </div>
      </article>
    </div>

    <div
      v-if="loading && users.length > 0"
      class="liked-page__more"
    >
      <loading-spinner />
    </div>
  </div>
</template>

<script>
import { throwDefaultError, showNotify } from '@/helpers/helpers'
import useJwt from '@/auth/jwt/useJwt'

export default {
  name: 'InteractionFavoriteUsers',
  props: {
    activeTabIndex: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      loading: false,
      scrollOffsetForLoadMore: 300,
      users: [],
      usersLimit: 100,
      usersOffset: 0,
      usersAllLoaded: false,
      totalCount: null,
      busyIds: [],
    }
  },
  computed: {
    storeUsers() {
      return this.$store.getters['users/users']
    },
    activeCount() {
      return Math.max(0, this.totalCount || 0)
    },
  },
  watch: {
    storeUsers: {
      handler(value) {
        if (!Array.isArray(value)) return
        this.users.forEach(user => {
          const storeUser = value.find(u => u.uuid === user.id)
          if (!storeUser || !user.profile) return
          if (storeUser.status === 'USER_ONLINE') this.$set(user.profile, 'onlineStatus', 'online')
          else if (storeUser.status === 'USER_OFFLINE') this.$set(user.profile, 'onlineStatus', 'offline')
          else if (storeUser.status === 'USER_BUSY') this.$set(user.profile, 'busyInCall', true)
          else if (storeUser.status === 'USER_AVAILABLE') this.$set(user.profile, 'busyInCall', false)
        })
      },
      immediate: true,
      deep: true,
    },
  },
  created() {
    this.getUsers()
    window.addEventListener('scroll', this.handleScroll, { passive: true })
  },
  // Sayfa önbellekten (keep-alive) tekrar açılırsa listeyi sunucudan yeniden çek;
  // başka bir yerde beğeni kaldırıldıysa liste güncel olur.
  activated() {
    this.reload()
  },
  destroyed() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    getUsers(clear = true) {
      this.loading = true
      useJwt.favoriteUsers(this.usersOffset, this.usersLimit)
        .then(response => {
          const body = (response && response.data && response.data.body) || {}
          const list = (body.users || []).map(u => ({ ...u, fav: true }))
          if (clear) this.users = list
          else this.users.push(...list)
          this.usersOffset = (body.offset || 0) + (body.limit || this.usersLimit)
          this.totalCount = typeof body.totalCount === 'number' ? body.totalCount : this.users.length
          if (this.usersOffset >= this.totalCount || list.length === 0) this.usersAllLoaded = true
        })
        .catch(error => {
          throwDefaultError(this.$toast, error)
        })
        .finally(() => { this.loading = false })
    },
    reload() {
      this.usersOffset = 0
      this.usersAllLoaded = false
      this.getUsers(true)
    },
    handleScroll() {
      if (this.activeTabIndex !== 1 || this.usersAllLoaded || this.loading) return
      if ((window.innerHeight + window.scrollY + this.scrollOffsetForLoadMore) >= document.body.scrollHeight) {
        this.getUsers(false)
      }
    },
    profileRoute(user) {
      return { name: 'users.show', params: { id: user.id } }
    },
    age(user) {
      return new Date().getFullYear() - user.profile.year
    },
    isOnline(user) {
      return user.profile && user.profile.onlineStatus === 'online'
    },
    isBusy(user) {
      return this.busyIds.includes(user.id)
    },
    setBusy(user, busy) {
      this.busyIds = busy ? [...this.busyIds, user.id] : this.busyIds.filter(id => id !== user.id)
    },
    // Beğeniyi kaldır (takipten çık): kişi listeden çıkarılır
    unlike(user) {
      this.setBusy(user, true)
      useJwt.removeFavoriteUser(user.id)
        .then(() => {
          this.users = this.users.filter(u => u.id !== user.id)
          if (this.totalCount) this.totalCount -= 1
          if (this.usersOffset > 0) this.usersOffset -= 1
          showNotify(this.$toast, this.$t('Unliking successful'), 'success')
        })
        .catch(error => throwDefaultError(this.$toast, error))
        .finally(() => this.setBusy(user, false))
    },
  },
}
</script>

<style lang="scss" scoped>
.liked-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 8px 0 0;
}

.liked-page__head {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 22px;
}

.liked-page__icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: linear-gradient(135deg, #7c3aed, #ea03b2);
  flex-shrink: 0;
}

.liked-page__title {
  color: #fff !important;
  font-size: 24px;
  font-weight: 800;
  margin: 0;
}

.liked-page__subtitle {
  margin: 2px 0 0;
  color: rgba(236, 232, 255, 0.7);
  font-size: 14px;
}

.liked-page__state,
.liked-page__more {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}

.liked-page__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 48px 20px;
  text-align: center;
  border: 1px solid rgba(150, 110, 255, 0.24);
  border-radius: 20px;
  background: rgba(32, 22, 72, 0.6);

  p {
    margin: 0;
    color: rgba(236, 232, 255, 0.85);
    font-size: 16px;
  }
}

.liked-page__empty-icon {
  width: 68px;
  height: 68px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #f472b6;
  background: rgba(244, 114, 182, 0.12);
}

.liked-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 16px;
}

.liked-card {
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 18px;
  overflow: hidden;
  background: rgba(32, 22, 72, 0.72);
  border: 1px solid rgba(150, 110, 255, 0.22);
  box-shadow: 0 10px 30px rgba(6, 2, 24, 0.35);
  transition: transform 0.15s ease, border-color 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(244, 114, 182, 0.45);
  }
}

.liked-card__photo {
  position: relative;
  display: block;
  aspect-ratio: 3 / 4;
  background: #1a1238;
  transition: filter 0.2s ease;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 45% 0 0;
    background: linear-gradient(180deg, transparent, rgba(8, 4, 24, 0.88));
    pointer-events: none;
  }
}

.liked-card__initial {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 48px;
  font-weight: 800;
}

.liked-card__online {
  position: absolute;
  top: 10px;
  inset-inline-start: 10px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  background: rgba(8, 4, 24, 0.65);
  color: #fff;
  font-size: 11px;
  font-weight: 700;

  i {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #22c55e;
  }
}

.liked-card__info {
  position: absolute;
  inset-inline: 12px;
  bottom: 10px;
  z-index: 1;
  display: flex;
  flex-direction: column;
  color: #fff;

  strong {
    font-size: 15px;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  small {
    color: rgba(236, 232, 255, 0.75);
    font-size: 12px;
  }
}

.liked-card__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
}

.liked-card__message {
  flex: 1;
}

.liked-card__heart {
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  border: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: linear-gradient(135deg, #ea03b2, #f472b6);
  transition: transform 0.15s ease, opacity 0.15s ease;

  &:hover {
    transform: scale(1.06);
  }

  &:disabled {
    opacity: 0.6;
  }
}

.liked-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 38px;
  padding: 0 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none !important;
  white-space: nowrap;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
}

.liked-btn--ghost {
  border: 1px solid rgba(150, 110, 255, 0.35);
  background: rgba(15, 8, 38, 0.5);
  color: #fff !important;

  &:hover {
    border-color: rgba(244, 114, 182, 0.6);
  }
}

.liked-btn--primary {
  border: 0;
  background: linear-gradient(135deg, #7c3aed, #ea03b2);
  color: #fff !important;
  padding: 0 22px;
}

@media (max-width: 575px) {
  .liked-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .liked-page__title {
    font-size: 20px;
  }

  .liked-btn {
    padding: 0 10px;
    font-size: 12px;
  }
}
</style>
